import { useEffect, useState } from 'react';
import {
  Content,
  ContentHeader,
  DismissableBanner,
  Lifecycle,
  Link,
  SupportButton,
} from '@backstage/core-components';
import { Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import {
  EntityNode,
  EntityRelationsGraph as EntityRelationsGraphBase,
  EntityRelationsGraphProps,
} from '@backstage/plugin-catalog-graph';
import {
  CompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import ZoomOutMap from '@material-ui/icons/ZoomOutMap';
import { useRouteRef } from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EntityFilter } from './EntityFilter';
import { EntityRefOption } from '../../types';

const useStyles = makeStyles(theme => ({
  content: {
    minHeight: 0,
  },
  container: {
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
  },
  fullHeight: {
    maxHeight: '100%',
    display: 'flex',
    minHeight: 0,
  },
  graphWrapper: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
  },
  graph: {
    flex: 1,
    minHeight: 0,
  },
  legend: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    padding: theme.spacing(1),
    '& .icon': {
      verticalAlign: 'bottom',
    },
  },
  filters: {
    display: 'grid',
    gridGap: theme.spacing(1),
    gridAutoRows: 'auto',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
    [theme.breakpoints.only('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    [theme.breakpoints.only('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
  },
}));

export const EntityRelationsGraph = (
  props: Omit<
    EntityRelationsGraphProps,
    'className' | 'onNodeClick' | 'rootEntityNames'
  > & {
    title: string;
    options: EntityRefOption[];
  },
) => {
  const { title, options: initialEntityRefOptions, ...rest } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRootEntityRefs =
    searchParams
      .get('rootEntityRefs')
      ?.split(',')
      .map(ref => parseEntityRef(ref)) ?? [];
  const [rootEntityRefs, setRootEntityRefs] = useState<CompoundEntityRef[]>(
    selectedRootEntityRefs,
  );
  useEffect(() => {
    if (rootEntityRefs.length > 0) {
      setSearchParams({
        rootEntityRefs: rootEntityRefs
          .map(ref => stringifyEntityRef(ref))
          .join(','),
      });
    } else {
      setSearchParams({});
    }
  }, [rootEntityRefs, setSearchParams]);
  return (
    <Content stretch className={classes.content}>
      <DismissableBanner
        fixed
        variant="info"
        id="feature-entity-relations-graph-alpha"
        message={
          <Typography variant="body2">
            This catalog graphing feature is in <Lifecycle alpha />. It may
            change before it's officially launched. You can share feedback about
            this feature on Slack in{' '}
            <Link to="https://appfolio.enterprise.slack.com/archives/C03A29Y4H5Y">
              #developer-portal
            </Link>
            .
          </Typography>
        }
      />
      <ContentHeader title={title}>
        <SupportButton>
          <Typography paragraph variant="body1">
            This is an experimental catalog graph page.
          </Typography>
          <Typography paragraph variant="body1">
            The graph displays dependency connections between catalog entities.
            The graph's nodes and connections move from left to right, with
            consumer nodes on the left and dependency nodes on the right of each
            connection.
          </Typography>
        </SupportButton>
      </ContentHeader>
      <Grid container className={classes.container}>
        <Grid item xs={12} lg={2} className={classes.filters}>
          <EntityFilter
            value={rootEntityRefs}
            options={initialEntityRefOptions}
            onChange={setRootEntityRefs}
          />
        </Grid>
        <Grid item xs className={classes.fullHeight}>
          <Paper className={classes.graphWrapper} tabIndex={0}>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              className={classes.legend}
            >
              <ZoomOutMap className="icon" />
              Use pinch &amp; zoom to move around the diagram. Click to navigate
              to the entity.
            </Typography>
            <EntityRelationsGraphBase
              rootEntityNames={
                rootEntityRefs.length ? rootEntityRefs : initialEntityRefOptions
              }
              className={classes.graph}
              onNodeClick={(node: EntityNode) => {
                const nodeEntityName = parseEntityRef(node.id);
                const path = catalogEntityRoute({
                  kind: nodeEntityName.kind.toLocaleLowerCase('en-US'),
                  namespace:
                    nodeEntityName.namespace.toLocaleLowerCase('en-US'),
                  name: nodeEntityName.name,
                });
                navigate(path);
              }}
              maxDepth={undefined}
              {...rest}
            />
          </Paper>
        </Grid>
      </Grid>
    </Content>
  );
};
